import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BedDouble, DoorOpen, Home, Receipt, ShieldAlert, UserCheck, Users, Wrench } from "lucide-react";

import { hostelService } from "../services/hostelService";
import { usePermissions } from "../hooks/usePermissions";
import { todayIso } from "../utils/dates";
import { BoardersPanel } from "../components/hostel/BoardersPanel";
import { FeesPanel } from "../components/hostel/FeesPanel";
import { RepairsPanel } from "../components/hostel/RepairsPanel";
import { ResidencesPanel } from "../components/hostel/ResidencesPanel";
import { RoomsPanel } from "../components/hostel/RoomsPanel";
import { VisitorsPanel } from "../components/hostel/VisitorsPanel";
import { WelfarePanel } from "../components/hostel/WelfarePanel";
import { isInside } from "../components/hostel/hostelLogic";

type Tab = "boarders" | "rooms" | "residences" | "visitors" | "repairs" | "welfare" | "fees";
const KEYS = ["hostels", "hostel-rooms", "hostel-beds", "hostel-allocations", "hostel-fees", "hostel-payments", "hostel-visitors", "hostel-maintenance", "hostel-welfare", "hostel-stats", "hostel-occupancy", "hostel-active-visitors"];

/** Boarding: who sleeps where, the residences and rooms, visitors, repairs, welfare and fees. */
export default function HostelPage() {
  const { hasCapability } = usePermissions();
  const canManage = hasCapability("access.hostel.boarding_manager");
  const canOperate = hasCapability("access.hostel.boarding_ops");
  const seesFees = hasCapability("access.hostel.boarding_finance_read");
  const isFamily = !canOperate;
  const tabs: { id: Tab; label: string; icon: typeof Home }[] = [
    { id: "boarders", label: isFamily ? "Boarding place" : "Boarders", icon: Users },
    ...(canOperate ? [{ id: "rooms" as const, label: "Rooms", icon: DoorOpen }] : []),
    { id: "residences", label: isFamily ? "Residence" : "Residences", icon: Home },
    ...(canOperate ? [{ id: "visitors" as const, label: "Visitors", icon: UserCheck }, { id: "repairs" as const, label: "Repairs", icon: Wrench }] : []),
    { id: "welfare", label: "Welfare", icon: ShieldAlert },
    ...(seesFees ? [{ id: "fees" as const, label: "Fees", icon: Receipt }] : []),
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "boarders";
  const select = (next: Tab) => setParams(next === "boarders" ? {} : { tab: next }, { replace: true });

  const queryClient = useQueryClient();
  const hostels = useQuery({ queryKey: ["hostels"], queryFn: hostelService.getHostels });
  const allocations = useQuery({ queryKey: ["hostel-allocations"], queryFn: hostelService.getAllocations });
  const rooms = useQuery({ queryKey: ["hostel-rooms"], queryFn: hostelService.getRooms, enabled: canOperate });
  const beds = useQuery({ queryKey: ["hostel-beds"], queryFn: hostelService.getBeds, enabled: canOperate });
  const welfare = useQuery({ queryKey: ["hostel-welfare"], queryFn: hostelService.getWelfareCases });
  const visitors = useQuery({ queryKey: ["hostel-visitors"], queryFn: hostelService.getVisitors, enabled: canOperate });
  const repairs = useQuery({ queryKey: ["hostel-maintenance"], queryFn: hostelService.getMaintenance, enabled: canOperate });
  const fees = useQuery({ queryKey: ["hostel-fees"], queryFn: hostelService.getFees, enabled: seesFees });
  const charges = useQuery({ queryKey: ["hostel-payments"], queryFn: hostelService.getPayments, enabled: seesFees && tab === "fees" });
  const stats = useQuery({ queryKey: ["hostel-stats"], queryFn: hostelService.getStats });
  const learners = useQuery({ queryKey: ["hostel-students"], queryFn: hostelService.getStudents, enabled: canOperate, staleTime: 5 * 60_000 });
  const refresh = () => Promise.all(KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const today = todayIso();
  const places = allocations.data ?? [];
  const boarding = places.filter((row) => row.status === "active");
  const waiting = places.filter((row) => row.status === "pending").length;
  const inside = (visitors.data ?? []).filter((row) => isInside(row, today)).length;
  const visitsToday = (visitors.data ?? []).filter((row) => row.visit_date === today).length;
  const openRepairs = (repairs.data ?? []).filter((row) => row.status === "pending" || row.status === "in_progress").length;
  const needsAction = (welfare.data ?? []).filter((row) => !row.is_resolved && !row.action_taken).length;
  const openWelfare = (welfare.data ?? []).filter((row) => !row.is_resolved).length;
  const dash = (query: { isLoading: boolean }, text: string) => (query.isLoading ? "–" : text);
  const mine = boarding[0];

  const tiles: { label: string; value: string; note: string; tab: Tab; icon: typeof Home; alert?: boolean }[] = isFamily
    ? [
        { label: "Sleeps in", value: dash(allocations, mine ? `Room ${mine.room_number}, bed ${mine.bed_number}` : "No place"), note: mine ? mine.hostel_name : "Not boarding", tab: "boarders", icon: BedDouble },
        { label: "Open welfare cases", value: dash(welfare, String(openWelfare)), note: openWelfare ? "Being dealt with" : "Nothing open", tab: "welfare", icon: ShieldAlert },
      ]
    : [
        { label: "Boarders", value: dash(allocations, String(boarding.length)), note: waiting ? `${waiting} waiting for approval` : "None waiting", tab: "boarders", icon: Users, alert: canManage && waiting > 0 },
        { label: "Free beds", value: dash(stats, String(stats.data?.available_beds ?? 0)), note: stats.data ? `of ${stats.data.total_beds} set up · ${Math.round(stats.data.occupancy_rate)}% full` : "", tab: canOperate ? "rooms" : "residences", icon: BedDouble },
        { label: "Visitors inside", value: dash(visitors, String(inside)), note: `${visitsToday} today`, tab: "visitors", icon: UserCheck, alert: inside > 0 },
        { label: "Needs attention", value: dash(welfare, String(needsAction + openRepairs)), note: `${needsAction} welfare · ${openRepairs} repairs`, tab: needsAction ? "welfare" : "repairs", icon: ShieldAlert, alert: needsAction > 0 },
      ];
  const badges: Partial<Record<Tab, number>> = { boarders: canManage ? waiting : 0, visitors: inside, repairs: openRepairs, welfare: canManage ? needsAction : 0 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Boarding</h1>
        <p className="mt-1 text-sm text-gray-500">
          {canManage
            ? "Give learners beds, set up residences and rooms, sign visitors in and out, see to repairs and look after boarders' welfare."
            : canOperate
              ? "Sign visitors in and out, report repairs and welfare cases, and see who sleeps where."
              : "Where your child sleeps, the residence's routine and rules, and anything the matron has recorded."}
        </p>
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 ${tiles.length > 2 ? "lg:grid-cols-4" : ""}`}>
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={() => select(tile.tab)}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Boarding sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "boarders" && (
        <BoardersPanel
          allocations={allocations.data}
          learners={learners.data ?? []}
          hostels={hostels.data ?? []}
          rooms={rooms.data ?? []}
          beds={beds.data ?? []}
          fees={fees.data ?? []}
          isLoading={allocations.isLoading}
          isError={allocations.isError}
          onRetry={() => allocations.refetch()}
          canManage={canManage}
          isFamily={isFamily}
          onChanged={refresh}
        />
      )}
      {tab === "rooms" && canOperate && (
        <RoomsPanel rooms={rooms.data} beds={beds.data ?? []} hostels={hostels.data ?? []} isLoading={rooms.isLoading} isError={rooms.isError} onRetry={() => rooms.refetch()} canManage={canManage} onChanged={refresh} />
      )}
      {tab === "residences" && (
        <ResidencesPanel hostels={hostels.data} isLoading={hostels.isLoading} isError={hostels.isError} onRetry={() => hostels.refetch()} canManage={canManage} canOperate={canOperate} onChanged={refresh} />
      )}
      {tab === "visitors" && canOperate && (
        <VisitorsPanel visitors={visitors.data} boarders={places} isLoading={visitors.isLoading} isError={visitors.isError} onRetry={() => visitors.refetch()} onChanged={refresh} />
      )}
      {tab === "repairs" && canOperate && (
        <RepairsPanel jobs={repairs.data} hostels={hostels.data ?? []} rooms={rooms.data ?? []} isLoading={repairs.isLoading} isError={repairs.isError} onRetry={() => repairs.refetch()} onChanged={refresh} />
      )}
      {tab === "welfare" && (
        <WelfarePanel
          cases={welfare.data}
          boarders={places}
          isLoading={welfare.isLoading}
          isError={welfare.isError}
          onRetry={() => welfare.refetch()}
          canManage={canManage}
          canReport={canOperate}
          isFamily={isFamily}
          onChanged={refresh}
        />
      )}
      {tab === "fees" && seesFees && (
        <FeesPanel
          fees={fees.data}
          charges={charges.data}
          hostels={hostels.data ?? []}
          isLoading={fees.isLoading}
          isError={fees.isError}
          onRetry={() => fees.refetch()}
          canManage={canManage}
          isFamily={isFamily}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
